import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { ImageLibraryContext, IMAGE_POOLS, normalizeImageEntry } from '../DataContexts/ImageLibraryContext';
import './AdImagePicker.css';

const S3_BASE = 'https://tdj-s3.s3.eu-west-2.amazonaws.com/';

/** Legacy base of the ads pool; kept for callers that build ad image URLs themselves. */
const IMAGE_BASE_URL = `${S3_BASE}/advertenties/`;

/** Fallback image URL per pool, only used when the server sends no url for an entry. */
const POOL_BASE_URLS = Object.freeze({
  [IMAGE_POOLS.ADS]: IMAGE_BASE_URL,
});

const POOL_TITLES = Object.freeze({
  [IMAGE_POOLS.ADS]: 'Choose image',
});

// How long to wait before re-reading the pool once more after an upload whose
// file the server did not list yet (the ads upload finishes asynchronously on
// the server, so the first re-read right after the upload can still miss it).
const UPLOAD_RECHECK_DELAY_MS = 2000;

/** File name of an uploaded image: last path segment of the URL the server answered. */
export const fileNameFromUploadResult = (result, file) => {
  if (result && typeof result === 'object') {
    const name = result.fileName ?? result.name;
    if (name) return String(name);
  }
  if (typeof result === 'string' && result.trim()) {
    const withoutQuery = result.trim().split(/[?#]/)[0];
    const last = withoutQuery.split('/').filter(Boolean).pop();
    if (last) {
      try {
        return decodeURIComponent(last);
      } catch {
        return last;
      }
    }
  }
  // Mirrors the server's AmazonClient.generateFileName (spaces → underscores)
  return file?.name ? file.name.replace(/ /g, '_') : '';
};

const urlFromUploadResult = (result) => {
  if (result && typeof result === 'object' && result.url) return result.url;
  if (typeof result === 'string' && /^https?:\/\//i.test(result.trim())) return result.trim();
  return null;
};

/**
 * Shared image-picker popup (DJB-233), used by the Ads & Screensaver Editor
 * (pool "ads").
 *
 * The picker reads its pool itself through ImageLibraryContext
 * (GET /image-library/{pool}) each time it opens:
 *  - compact thumbnail grid with a name filter
 *  - upload (when `onUpload` is given): the uploaded file is shown in the grid
 *    and pre-selected immediately, before the server lists it
 *  - Save/double-click hands the chosen file name to `onSelect` (when given)
 *
 * Images are never deleted from this popup; they are removed together with
 * the concept that owns them.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {'ads'} [props.pool='ads']
 * @param {string} [props.title]                 defaults per pool
 * @param {(fileName: string, entry: Object) => void} [props.onSelect]
 *        when absent the popup is a browse dialog without Save
 * @param {(file: File) => Promise<string|Object>} [props.onUpload]
 *        resolves with the URL the server answered; when absent the Upload
 *        button is hidden
 * @param {string} [props.uploadLabel='Upload image']
 *        label of the upload button
 */
const AdImagePicker = ({
  isOpen,
  onClose,
  pool = IMAGE_POOLS.ADS,
  title,
  onSelect,
  onUpload,
  uploadLabel = 'Upload image',
}) => {
  const { fetchImageLibrary } = useContext(ImageLibraryContext);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedName, setSelectedName] = useState(null);
  const [search, setSearch] = useState('');

  const fileInputRef = useRef(null);
  const requestIdRef = useRef(0);
  const recheckTimerRef = useRef(null);
  const openRef = useRef(isOpen);
  openRef.current = isOpen;

  const clearRecheck = () => {
    if (recheckTimerRef.current) {
      clearTimeout(recheckTimerRef.current);
      recheckTimerRef.current = null;
    }
  };

  /**
   * Re-reads the pool. Locally added (pending) entries the server does not list
   * yet stay in the grid, at the front, so a fresh upload never disappears.
   */
  const reload = useCallback(
    async ({ showLoading = false } = {}) => {
      const requestId = ++requestIdRef.current;
      if (showLoading) setLoading(true);
      try {
        const fresh = await fetchImageLibrary(pool);
        if (requestId !== requestIdRef.current || !openRef.current) return null;
        const list = Array.isArray(fresh)
          ? fresh.map((e) => normalizeImageEntry(e, pool)).filter(Boolean)
          : [];
        setEntries((prev) => {
          const freshNames = new Set(list.map((e) => e.fileName));
          const pendingKept = prev.filter((e) => e.pending && !freshNames.has(e.fileName));
          return [...pendingKept, ...list];
        });
        return list;
      } finally {
        if (showLoading && requestId === requestIdRef.current) setLoading(false);
      }
    },
    [fetchImageLibrary, pool]
  );

  // Fresh state + fresh list every time the popup opens
  useEffect(() => {
    if (!isOpen) {
      clearRecheck();
      return undefined;
    }
    setEntries([]);
    setSelectedName(null);
    setSearch('');
    reload({ showLoading: true });
    return () => {
      requestIdRef.current += 1;
      clearRecheck();
    };
  }, [isOpen, reload]);

  // Escape closes the popup
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const imageUrlOf = (entry) => entry.url || `${POOL_BASE_URLS[pool] ?? IMAGE_BASE_URL}${entry.fileName}`;

  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.fileName.toLowerCase().includes(q));
  }, [entries, search]);

  const selectedEntry = useMemo(
    () => entries.find((e) => e.fileName === selectedName) ?? null,
    [entries, selectedName]
  );

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onUpload) return;
    setUploading(true);
    try {
      const result = await onUpload(file);
      const fileName = fileNameFromUploadResult(result, file);
      if (fileName && openRef.current) {
        const optimistic = {
          pool,
          fileName,
          url: urlFromUploadResult(result) || `${POOL_BASE_URLS[pool] ?? IMAGE_BASE_URL}${fileName}`,
          pending: true,
        };
        // Show + select the new image at once; the re-read below replaces the
        // optimistic entry with the server's as soon as it is listed.
        setEntries((prev) => [optimistic, ...prev.filter((x) => x.fileName !== fileName)]);
        setSelectedName(fileName);
        setSearch('');
        const list = await reload();
        if (list && !list.some((x) => x.fileName === fileName)) {
          clearRecheck();
          recheckTimerRef.current = setTimeout(() => {
            recheckTimerRef.current = null;
            if (openRef.current) reload();
          }, UPLOAD_RECHECK_DELAY_MS);
        }
      }
    } catch {
      // notification already shown by the upload context
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    if (selectedEntry && onSelect) {
      onSelect(selectedEntry.fileName, selectedEntry);
      onClose?.();
    }
  };

  const handlePick = (entry) => {
    if (!onSelect) return;
    onSelect(entry.fileName, entry);
    onClose?.();
  };

  if (!isOpen) return null;

  const heading = title ?? POOL_TITLES[pool] ?? 'Choose image';
  const busy = uploading;

  let body;
  if (loading && entries.length === 0) {
    body = <p className="ad-image-picker-empty">Loading images…</p>;
  } else if (entries.length === 0) {
    body = <p className="ad-image-picker-empty">No images available</p>;
  } else if (filteredEntries.length === 0) {
    body = <p className="ad-image-picker-empty">No images match “{search.trim()}”</p>;
  } else {
    body = (
      <ul className="ad-image-picker-grid" aria-label="Images">
        {filteredEntries.map((entry) => {
          const isSelected = selectedName === entry.fileName;
          const classes = ['ad-image-picker-item'];
          if (isSelected) classes.push('ad-image-picker-item-selected');
          if (entry.pending) classes.push('ad-image-picker-item-pending');
          return (
            <li key={entry.fileName} className={classes.join(' ')}>
              <button
                type="button"
                className="ad-image-picker-item-select"
                aria-pressed={isSelected}
                title={entry.fileName}
                onClick={() => setSelectedName(entry.fileName)}
                onDoubleClick={() => handlePick(entry)}
              >
                <span className="ad-image-picker-thumb">
                  <img src={imageUrlOf(entry)} alt={entry.fileName} className="ad-image-picker-img" loading="lazy" />
                </span>
                <span className="ad-image-picker-name">{entry.fileName}</span>
              </button>
              {entry.pending && (
                <span className="ad-image-picker-badge ad-image-picker-badge-new">New</span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="ad-image-picker-overlay" onClick={onClose}>
      <div
        className="ad-image-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ad-image-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ad-image-picker-header">
          <div className="ad-image-picker-heading">
            <h3 id="ad-image-picker-title" className="ad-image-picker-title">
              {heading}
            </h3>
            {!loading && (
              <span className="ad-image-picker-count">
                {entries.length} {entries.length === 1 ? 'image' : 'images'}
              </span>
            )}
          </div>
          <button type="button" className="ad-image-picker-close" onClick={onClose} aria-label="Close dialog">
            ×
          </button>
        </div>

        <div className="ad-image-picker-toolbar">
          <input
            type="search"
            className="ad-image-picker-search"
            placeholder="Filter by name…"
            aria-label="Filter images by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="ad-image-picker-toolbar-actions">
            <button
              type="button"
              className="ad-image-picker-btn ad-image-picker-btn-ghost"
              onClick={() => reload({ showLoading: entries.length === 0 })}
              disabled={loading || busy}
            >
              Refresh
            </button>
            {onUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="ad-image-picker-file-input"
                  aria-label="Upload image file"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className="ad-image-picker-btn ad-image-picker-btn-upload"
                  onClick={handleUploadClick}
                  disabled={busy}
                >
                  {uploading ? 'Uploading…' : uploadLabel}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="ad-image-picker-content">{body}</div>

        <div className="ad-image-picker-footer">
          <div className="ad-image-picker-footer-left">
            {selectedEntry ? (
              <span className="ad-image-picker-selected-name" title={selectedEntry.fileName}>
                Selected: <strong>{selectedEntry.fileName}</strong>
              </span>
            ) : (
              <span className="ad-image-picker-selected-name ad-image-picker-selected-none">
                {onSelect ? 'Select an image' : ''}
              </span>
            )}
          </div>
          <div className="ad-image-picker-footer-right">
            <button type="button" className="ad-image-picker-btn ad-image-picker-btn-cancel" onClick={onClose}>
              {onSelect ? 'Cancel' : 'Close'}
            </button>
            {onSelect && (
              <button
                type="button"
                className="ad-image-picker-btn ad-image-picker-btn-save"
                onClick={handleSave}
                disabled={!selectedEntry || busy}
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdImagePicker;
export { IMAGE_BASE_URL, POOL_BASE_URLS };
